import {Injectable} from '@angular/core';
import {ProfileStorageService} from '../../services/profile/profile-storage.service';
import {ProfielResource} from '../../services/resources/profiel.resource';
import {Subject, Subscription} from 'rxjs';
import {Profiel, ProfielFunctie, ProfielOpleiding} from '../../model/profiel/profiel';
import parseISO from "date-fns/parseISO";

@Injectable({
  providedIn: 'root'
})
export class ProfileService {
  public constructor(private profileStorageService: ProfileStorageService, private profielResource: ProfielResource) {}

  public addOpleidingToProfile(data: any, profile: Profiel, subscription: Subscription): Subject<void> {
    const addOpleidingInProfileSubject: Subject<void> = new Subject<void>();

    if (!profile.opleidingen) {
      profile.opleidingen = [];
    }

    profile.opleidingen.push({
      ...data.opleiding,
      van: data.van instanceof Date ? data.van.toISOString() : this.convertFormattedDateStringToDate(data.van)?.toISOString(),
      eind: data.eind instanceof Date ? data.eind.toISOString() : this.convertFormattedDateStringToDate(data.eind)?.toISOString()
    });

    this.profielResource.updateProfiel(profile).subscribe(() => {
      this.profileStorageService.saveProfileInStorage(profile);
      this.profileStorageService.syncResponseMapWithProfile();
      addOpleidingInProfileSubject.next();
    });

    subscription.unsubscribe();
    return addOpleidingInProfileSubject;
  }

  public addFunctieToProfile(data: any, profile: Profiel, subscription: Subscription): Subject<void> {
    const addFunctieInProfileSubject: Subject<void> = new Subject<void>();

    if (!profile.functies) {
      profile.functies = [];
    }

    profile.functies.push({
      ...data.functie,
      van: data.van instanceof Date ? data.van.toISOString() : this.convertFormattedDateStringToDate(data.van)?.toISOString(),
      tot: data.tot instanceof Date ? data.tot.toISOString() : this.convertFormattedDateStringToDate(data.tot)?.toISOString()
    });

    this.profielResource.updateProfiel(profile).subscribe(() => {
      this.profileStorageService.saveProfileInStorage(profile);
      this.profileStorageService.syncResponseMapWithProfile();
      addFunctieInProfileSubject.next();
    });

    subscription.unsubscribe();
    return addFunctieInProfileSubject;
  }

  public removeOpleidingFromProfile(profile: Profiel, id: number): Subject<void> {
    const removeOpleidingInProfileSubject: Subject<void> = new Subject<void>();
    const opleiding: any = profile.opleidingen.find(item => item.id === id);
    if (opleiding) {
      const index = profile.opleidingen.indexOf(opleiding);
      if (index !== -1) {
        profile.opleidingen.splice(index, 1);
        this.profielResource.updateProfiel(profile).subscribe(() => {
          this.profileStorageService.saveProfileInStorage(profile);
          this.profileStorageService.syncResponseMapWithProfile();
          removeOpleidingInProfileSubject.next();
        });
      }
    }
    return removeOpleidingInProfileSubject;
  }

  public removeFunctieFromProfile(profile: Profiel, id: number): Subject<void> {
    const removeFunctieInProfileSubject: Subject<void> = new Subject<void>();
    const functie: any = profile.functies.find(item => item.id === id);
    if (functie) {
      const index = profile.functies.indexOf(functie);
      if (index !== -1) {
        profile.functies.splice(index, 1);
        this.profielResource.updateProfiel(profile).subscribe(() => {
          this.profileStorageService.saveProfileInStorage(profile);
          this.profileStorageService.syncResponseMapWithProfile();
          removeFunctieInProfileSubject.next();
        });
      }
    }
    return removeFunctieInProfileSubject;
  }

  public updateOpleidingInProfile(data: any, profile: Profiel, subscription: Subscription): Subject<void> {
    const updateOpleidingInProfileSubject: Subject<void> = new Subject<void>();
    const index = profile.opleidingen.findIndex((opleiding: ProfielOpleiding) => opleiding.id === data.opleiding.id);

    profile.opleidingen[index] = {
      ...data.opleiding,
      van: data.van instanceof Date ? data.van.toISOString() : this.convertFormattedDateStringToDate(data.van)?.toISOString(),
      eind: data.eind instanceof Date ? data.eind.toISOString() : this.convertFormattedDateStringToDate(data.eind)?.toISOString()
    };

    this.profielResource.updateProfiel(profile).subscribe(() => {
      this.profileStorageService.saveProfileInStorage(profile);
      this.profileStorageService.syncResponseMapWithProfile();
      updateOpleidingInProfileSubject.next();
    });
    subscription.unsubscribe();
    return updateOpleidingInProfileSubject;
  }

  public updateFunctieInProfile(data: any, profile: Profiel, subscription: Subscription): Subject<void> {
    const updateFunctieInProfileSubject: Subject<void> = new Subject<void>();
    const index = profile.functies.findIndex((functie: ProfielFunctie) => functie.id === data.functie.id);
    profile.functies[index] = {
      ...data.functie,
      van: data.van instanceof Date ? data.van.toISOString() : this.convertFormattedDateStringToDate(data.van)?.toISOString(),
      tot: data.tot instanceof Date ? data.tot.toISOString() : this.convertFormattedDateStringToDate(data.tot)?.toISOString()
    };

    this.profielResource.updateProfiel(profile).subscribe(() => {
      this.profileStorageService.saveProfileInStorage(profile);
      this.profileStorageService.syncResponseMapWithProfile();
      updateFunctieInProfileSubject.next();
    });
    subscription.unsubscribe();
    return updateFunctieInProfileSubject;
  }

  public updateInteresses(data: any, profile: Profiel, key: string, subscription: Subscription): Subject<void> {
    const updateInteressesSubject: Subject<void> = new Subject<void>();
    if (data) {
      const newSelection = data
        .filter(item => item.selected)
        .map(item => ({
          naam: item.name,
          esId: item.id,
          soort: key,
          uitgesloten: false,
          selected: item.selected
        }));

      profile.interesses = profile.interesses.filter(item => item.soort !== key).concat(newSelection);

      this.profielResource.updateProfiel(profile).subscribe(() => {
        this.profileStorageService.saveProfileInStorage(profile);
        this.profileStorageService.syncResponseMapWithProfile();
        updateInteressesSubject.next();
      });
    }
    subscription.unsubscribe();
    return updateInteressesSubject;
  }

  public convertFormattedDateStringToDate(dateAsFormattedString: string): Date {
    if (dateAsFormattedString) {
      const date = parseISO(dateAsFormattedString);
      return new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    }
    return null;
  }
}
